/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IContentBlockViewProps } from './dobbies-new-content-block';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { Image } from '@msdyn365-commerce/core';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { HeadingSettingsFontWeight, IDobbiesNewContentBlockProps } from './dobbies-new-content-block.props.autogenerated';
import { IDobbiesContentBlockData } from './dobbies-new-content-block.data';
import { ICategoryHierarchyViewProps } from '../../modules/dobbies-search-result-container/dobbies-search-result-container';
import { _getScreenSize } from '../../themes/dobbies/utilities/display-utils';

const ContentBlockView: React.FC<IContentBlockViewProps & IDobbiesNewContentBlockProps<IDobbiesContentBlockData>> = props => {
    const {
        config: { isCategoryNameTitle, hideClubPlusBanner, clubPlusTierId, heading, headingSettings },
        data: {
            modulesSharedState: { result: modulesSharedState }
        },
        contentBlockContainer,
        imageContainer,
        detailsContainer,
        text,
        links,
        image,
        contentBlockAnchorTag,
        imageLink,
        imageAriaLabel,
        context,
        loyaltyBoltonsDialog
    } = props;

    const {
        request: {
            urlTokens: { pageType }
        }
    } = context;

    /* VSI Cutomization - START - 11/11/20 */
    const [isMobile, SetIsMobile] = useState(false);
    const [categoryName, SetCategoryName] = useState<string | undefined>();
    const [isClubPlus, SetIsClubPlus] = useState<boolean | undefined>(undefined);
    const [isImageAvailable, setIsImageAvailable] = useState<boolean | undefined>();

    const fontWeightMap: Record<string, number> = {
        [HeadingSettingsFontWeight.Medium500]: 500,
        [HeadingSettingsFontWeight.Bold700]: 700
    };

    const renderHeading = (): React.ReactNode => {
        if (!heading) {
            return null;
        }

        const headingElement = (
            <Node
                className={classnames('ms-content-block__title', {
                    'ms-content-block__title--underline-on-hover': headingSettings?.underlineOnHover
                })}
                tag={heading.tag ?? 'h2'}
                style={
                    headingSettings?.fontWeight && {
                        fontWeight: `${fontWeightMap[headingSettings.fontWeight]} !important`
                    }
                }
                dangerouslySetInnerHTML={{ __html: heading.text }}
            />
        );

        if (headingSettings?.linkUrl && headingSettings.linkUrl !== '') {
            return (
                <a href={headingSettings.linkUrl} className='ms-content-block__title-link'>
                    {headingElement}
                </a>
            );
        }

        return headingElement;
    };

    const renderCategoryHierarchy = (categoryHeirarchyData: ICategoryHierarchyViewProps): JSX.Element | null => {
        const { CategoryHierarchyContainer, categoryHierarchyList, categoryHierarchySeparator } = categoryHeirarchyData;
        if (categoryHierarchyList) {
            return (
                <Node {...CategoryHierarchyContainer} className='ms-search-result-container__category-hierarchy'>
                    {categoryHierarchyList.map((category, index) => (
                        <React.Fragment key={index}>
                            {category}
                            {categoryHierarchyList && categoryHierarchyList[index + 1] && categoryHierarchySeparator}
                        </React.Fragment>
                    ))}
                </Node>
            );
        }

        return null;
    };

    useEffect(() => {
        const screenSize = _getScreenSize(context);
        const _isMobile = screenSize === 1 || screenSize === 2;
        SetIsMobile(_isMobile);

        // Check if pageType is Category and state is not set yet, get its category name
        if (categoryName === undefined && pageType && pageType.toLowerCase() === 'category') {
            if (isCategoryNameTitle) {
                SetCategoryName(modulesSharedState?.categoryName);
            }
            if (isImageAvailable === undefined) {
                const isImage = props.config.image ? true : false;
                setIsImageAvailable(isImage);
            }
        }
        // Check if clubplus banner is set to hide on my account page for club plus members and state is not set yet i.e. undefined
        if (hideClubPlusBanner && isClubPlus === undefined) {
            modulesSharedState?.customerLoyaltyTierId === clubPlusTierId?.toLowerCase() ? SetIsClubPlus(true) : SetIsClubPlus(false);
        }
    });

    // Dont show banner for club plus members on my account or if tier id is not set yet
    if (hideClubPlusBanner && (isClubPlus === undefined || isClubPlus === true)) {
        return <></>;
    }

    const mobileImage = _renderImageForMobile(props);

    const categoryHeading = renderHeading();

    /* VSI Cutomization - END */
    const checkClassName = () => {
        if (isImageAvailable === true && isMobile === false) {
            return classnames(contentBlockContainer.className, '');
        } else if (isImageAvailable === true && isMobile === true) {
            return classnames(contentBlockContainer.className, '');
        } else if (isImageAvailable === false && isMobile === false) {
            return classnames(contentBlockContainer.className, '');
        } else if (isImageAvailable === false && isMobile === true) {
            return classnames(contentBlockContainer.className, '');
        } else {
            return classnames(contentBlockContainer.className, '');
        }
    };

    if (imageLink) {
        return (
            <>
                {props.categoryHierarchy ? (
                    <div className='new-filter-ms-search-result-container ms-search-result-container'>
                        {renderCategoryHierarchy(props.categoryHierarchy)}
                    </div>
                ) : (
                    <></>
                )}
                <Module {...contentBlockContainer} className={checkClassName()}>
                    <Node
                        {...contentBlockAnchorTag}
                        href={imageLink}
                        className={contentBlockAnchorTag ? contentBlockAnchorTag.className : ''}
                        aria-label={imageAriaLabel}
                    >
                        <Node {...imageContainer}>{isMobile === true && mobileImage ? mobileImage : image}</Node>
                    </Node>
                    <Node {...detailsContainer}>
                        {categoryHeading}
                        {text}
                        {links}
                    </Node>
                </Module>
                {loyaltyBoltonsDialog ? loyaltyBoltonsDialog : <></>}
            </>
        );
    } else {
        return (
            <>
                {props.categoryHierarchy ? (
                    <div className='new-filter-ms-search-result-container ms-search-result-container'>
                        {renderCategoryHierarchy(props.categoryHierarchy)}
                    </div>
                ) : (
                    <></>
                )}
                <Module {...contentBlockContainer} className={checkClassName()}>
                    <Node {...imageContainer}>{isMobile === true && mobileImage ? mobileImage : image}</Node>
                    <Node {...detailsContainer}>
                        {categoryHeading}
                        {text}
                        {links}
                    </Node>
                </Module>
                {loyaltyBoltonsDialog ? loyaltyBoltonsDialog : <></>}
            </>
        );
    }
};

/* Following function renders if mobile view has different image than that of desktop image. */
const _renderImageForMobile = (props: IContentBlockViewProps & IDobbiesNewContentBlockProps<{}>): JSX.Element | undefined => {
    const {
        config: { imageForMobileIpad },
        context: { request }
    } = props;
    if (imageForMobileIpad === undefined) {
        return undefined;
    }

    return (
        <Image
            className={'promotion-mobile-image'}
            {...imageForMobileIpad}
            gridSettings={request.gridSettings!}
            imageSettings={imageForMobileIpad.imageSettings}
            loadFailureBehavior='default'
            role='tabpanel'
            id={`${props.id}__static-image`}
        />
    );
};

export default ContentBlockView;
